/**
 * Analytics Data Context
 *
 * Centralizes all API orchestration and data management for analytics.
 * Handles sequential API loading, key metrics transformation, and loading/error states.
 *
 * CRITICAL: Sequential API Loading Pattern
 * ========================================
 * APIs are loaded in this specific order for performance optimization:
 * 1. Invoice Data (loads first)
 * 2. Shipment Data (enabled after invoice completes)
 * 3. Chart Data (enabled after shipment completes)
 * 4. Charge Data (enabled after chart completes)
 *
 * This ensures efficient resource usage and progressive data display.
 */

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { KeyMetricsResponseDto, FiltersDataDto } from 'src/generated/services/TFinancialApi';
import { useChart, useKeyMetricsChargeData, useKeyMetricsInvoiceData, useKeyMetricsShipmentData, useFilters } from '../useAnalytics';
import { keyMetricsMapper } from '../analyticsMapper';
import { ICardAnalyticItemProps } from '../../../molecules/card/cardAnalytic/types';
import { CardId } from '../generalOverview/types';
import { useTableFilterState } from '../table/hooks/useTableFilterState';
import { UnifiedAnalyticsFilters } from '../table/analyticsTableFilterTypes';
import { mergeTableFiltersIntoRequest } from '../table/analyticsSearchRequestBuilders';

// Type alias for clarity - using generated type
type KeyMetricsResponse = KeyMetricsResponseDto;

export type ViewTab = 'chart' | 'table';

// Type alias for table types
export type TableType = 'shipments' | 'invoices' | 'charges';

interface TableTotals {
  shipments: number | null;
  invoices: number | null;
  charges: number | null;
}

interface ChartResponse {
  aggregated?: any[];
  dateRange?: {
    from?: string;
    to?: string;
  };
}

interface AnalyticsDataContextValue {
  // Raw API Response
  keyMetricsResponse?: KeyMetricsResponse;
  filteredKeyMetricsResponse?: KeyMetricsResponse;

  // Individual API Responses (for accessing specific data per card type)
  baseInvoiceData?: KeyMetricsResponse;
  baseShipmentData?: KeyMetricsResponse;
  baseChargeData?: KeyMetricsResponse;
  filteredInvoiceData?: KeyMetricsResponse;
  filteredShipmentData?: KeyMetricsResponse;
  filteredChargeData?: KeyMetricsResponse;

  // Processed/Mapped Key Metrics (for UI cards)
  keyMetrics: ICardAnalyticItemProps[];

  // Chart Data
  chartData?: ChartResponse;

  // Filter Data (Delayed Load)
  filterMetadata?: FiltersDataDto;
  isLoadingFilters: boolean;

  // View State
  activeTab: ViewTab;
  setActiveTab: (tab: ViewTab) => void;

  // Individual Loading States
  isKeyMetricsLoading: boolean;
  isChartLoading: boolean;
  cardLoadingStates: Record<CardId, boolean>;

  // Individual Error States
  cardErrorStates: Record<CardId, boolean>;
  isChartError: boolean;

  // Request Objects (for reference)
  baseSearchRequest: any;
  chartSearchRequest: any;

  // Table Filter State (merged from AnalyticsTableContext)
  filters: UnifiedAnalyticsFilters;
  drawerFilters: UnifiedAnalyticsFilters; // Base filters from drawer (for distinguishing table-only filters)
  activeFilterCount: number;
  updateFilter: (columnId: string, selectedValues: string[]) => void;
  clearAllFilters: () => void;

  // Table Totals
  totals: TableTotals;
  updateTotal: (tableType: TableType, total: number) => void;
  freshTabs: Set<TableType>;
}

const AnalyticsDataContext = createContext<AnalyticsDataContextValue | undefined>(undefined);

interface AnalyticsDataProviderProps {
  children: ReactNode;
  baseSearchRequest: any;
  chartSearchRequest: any;
  initialTableFilters?: Partial<UnifiedAnalyticsFilters>;
}

/**
 * Helper function to merge table column filters into the base search request
 * Only applies when in table view to ensure card metrics reflect filtered data
 */

export const AnalyticsDataProvider = ({ children, baseSearchRequest, chartSearchRequest, initialTableFilters }: AnalyticsDataProviderProps) => {
  // ========================================
  // VIEW STATE
  // ========================================
  const [activeTab, setActiveTab] = useState<ViewTab>('chart');

  // Extract filters from baseSearchRequest (FilterDrawer values)
  // This allows FilterDrawer selections to appear in table column filter dropdowns
  const baseFilters = baseSearchRequest.filters || {};
  const shipmentFilters = baseFilters.shipmentFilters || {};
  const tradeLaneFilters = baseFilters.tradeLaneFilters || {};

  // ========================================
  // TABLE FILTER STATE (merged from AnalyticsTableContext)
  // ========================================
  const defaultTableFilters: UnifiedAnalyticsFilters = useMemo(
    () => ({
      // Shipment & Shared - Initialize with FilterDrawer values
      shipmentNumber: shipmentFilters.shipmentNumber || [],
      mbl: shipmentFilters.mbl || [],
      serviceType: shipmentFilters.serviceType || [],
      invoiceNumber: shipmentFilters.invoiceNumber || [],
      serviceProviderCode: shipmentFilters.serviceProviderCode || [],
      modality: shipmentFilters.modality || [],
      billedEntity: shipmentFilters.billedEntity || [],

      // Trade Lane filters from FilterDrawer
      pol: tradeLaneFilters.pol || [],
      pod: tradeLaneFilters.pod || [],

      // Other shipment filters
      shipperName: shipmentFilters.shipperName || [],
      shipperCountry: shipmentFilters.shipperCountry || [],
      consigneeCountry: shipmentFilters.consigneeCountry || [],
      consigneeName: shipmentFilters.consigneeName || [],
      goodsName: shipmentFilters.goodsName || [],
      vehicleId: shipmentFilters.vehicleId || [],
      reviewStage: shipmentFilters.reviewStage || [],
      shipmentStatus: shipmentFilters.shipmentStatus || [],
      shipmentStatusReason: shipmentFilters.shipmentStatusReason || [],
      // Invoice Specific
      fapRef: [],
      accountNumber: [],
      invoiceStatus: [],
      invoiceReason: [],
      // Charge Specific
      chargeDomain: [],
      chargeName: [],
      legType: [],
      quantityUnit: [],
      chargeStatus: [],
      chargeStatusReason: [],
      shipmentChargesCount: [],
      invoiceChargesCount: [],
    }),
    [shipmentFilters, tradeLaneFilters]
  );

  const { tableFilters, handleColumnFilter, overrideAllFilters } = useTableFilterState<UnifiedAnalyticsFilters>({
    initialFilters: {
      ...defaultTableFilters,
      ...initialTableFilters,
    },
    filterToSelectionMap: (filters) => ({}), // Handled by child tables
    columnIdToFilterKey: (columnId) => {
      // Map standard UI column IDs to filter keys
      if (columnId === 'serviceProvider') return 'serviceProviderCode';
      if (columnId === 'shipper') return 'shipperName';
      if (columnId === 'consignee') return 'consigneeName';
      if (columnId === 'shipmentCurrentDeviationWorkflow') return 'reviewStage';
      if (columnId === 'reason') return 'invoiceReason';
      if (columnId === 'status') return 'invoiceStatus';
      return columnId;
    },
  });

  // Helper function to check if a filter value differs from drawer base filter
  const isTableOnlyFilter = useCallback(
    (key: keyof UnifiedAnalyticsFilters, tableValue: string[]): boolean => {
      const baseValue = defaultTableFilters[key];

      if (!Array.isArray(tableValue) || tableValue.length === 0) {
        return false;
      }

      // Check if this filter differs from the drawer filter
      return !baseValue || baseValue.length === 0 || tableValue.length !== baseValue.length || !tableValue.every((val, idx) => val === baseValue[idx]);
    },
    [defaultTableFilters]
  );

  // Calculate active filter count - only count filters that differ from drawer base filters
  // Drawer filters are part of the base query and should not show as "active" table filters
  const activeFilterCount = useMemo(() => {
    let count = 0;

    // Compare each filter in tableFilters with defaultTableFilters (drawer filters)
    (Object.keys(tableFilters) as Array<keyof UnifiedAnalyticsFilters>).forEach((key) => {
      if (isTableOnlyFilter(key, tableFilters[key])) {
        count++;
      }
    });

    return count;
  }, [tableFilters, isTableOnlyFilter]);

  // Expose base filters so tables can distinguish drawer filters from table filters
  const drawerFilters = defaultTableFilters;

  // Table totals state
  const [totals, setTotals] = useState<TableTotals>({
    shipments: null,
    invoices: null,
    charges: null,
  });

  // Track which tabs have fresh totals for current filter state
  const [freshTabs, setFreshTabs] = useState<Set<TableType>>(new Set());

  // Track previous filter state to detect changes
  const prevFiltersRef = useRef<string>(JSON.stringify(tableFilters));

  // Track previous baseSearchRequest to detect global filter changes
  const prevBaseSearchRequestRef = useRef<string>(JSON.stringify(baseSearchRequest.filters || {}));

  // Sync table filters with global filters - reset table filters when drawer filters change
  useEffect(() => {
    const currentBaseFiltersString = JSON.stringify(baseSearchRequest.filters || {});
    if (prevBaseSearchRequestRef.current !== currentBaseFiltersString) {
      // Drawer filters changed - reset ALL table filters and only keep drawer filter values
      const resetFilters: UnifiedAnalyticsFilters = {
        // Shipment & Shared - Only from drawer
        shipmentNumber: shipmentFilters.shipmentNumber || [],
        mbl: shipmentFilters.mbl || [],
        serviceType: shipmentFilters.serviceType || [],
        invoiceNumber: shipmentFilters.invoiceNumber || [],
        serviceProviderCode: shipmentFilters.serviceProviderCode || [],
        modality: shipmentFilters.modality || [],
        billedEntity: shipmentFilters.billedEntity || [],
        pol: tradeLaneFilters.pol || [],
        pod: tradeLaneFilters.pod || [],
        shipperName: shipmentFilters.shipperName || [],
        shipperCountry: shipmentFilters.shipperCountry || [],
        consigneeCountry: shipmentFilters.consigneeCountry || [],
        consigneeName: shipmentFilters.consigneeName || [],
        goodsName: shipmentFilters.goodsName || [],
        vehicleId: shipmentFilters.vehicleId || [],
        reviewStage: shipmentFilters.reviewStage || [],
        shipmentStatus: shipmentFilters.shipmentStatus || [],
        shipmentStatusReason: shipmentFilters.shipmentStatusReason || [],
        // Invoice Specific - Reset to empty (table-only filters)
        fapRef: [],
        accountNumber: [],
        invoiceStatus: [],
        invoiceReason: [],
        // Charge Specific - Reset to empty (table-only filters)
        chargeDomain: [],
        chargeName: [],
        legType: [],
        quantityUnit: [],
        chargeStatus: [],
        chargeStatusReason: [],
        shipmentChargesCount: [],
        invoiceChargesCount: [],
      };

      overrideAllFilters(resetFilters);
      prevBaseSearchRequestRef.current = currentBaseFiltersString;
    }
  }, [baseSearchRequest.filters, shipmentFilters, tradeLaneFilters, overrideAllFilters]);

  // When filters change, mark all tabs as having stale data
  useEffect(() => {
    const currentFiltersString = JSON.stringify(tableFilters);
    if (prevFiltersRef.current !== currentFiltersString) {
      setFreshTabs(new Set());
      prevFiltersRef.current = currentFiltersString;
    }
  }, [tableFilters]);

  // Update total for a specific table type and mark it as fresh
  const updateTotal = useCallback((tableType: TableType, total: number) => {
    setTotals((prev) => ({ ...prev, [tableType]: total }));
    setFreshTabs((prev) => new Set(prev).add(tableType));
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    Object.keys(tableFilters).forEach((columnId) => {
      handleColumnFilter(columnId, []);
    });
  }, [tableFilters, handleColumnFilter]);

  // ========================================
  // FILTER MERGING BASED ON ACTIVE TAB
  // ========================================
  // We default to 'invoice' context for the global metrics request since we don't track the active table tab here.
  // This means chargeCount will be mapped to the top-level chargeCountSet, which preserves legacy behavior for metrics cards.
  const finalSearchRequest = useMemo(() => {
    if (activeTab === 'table') {
      return mergeTableFiltersIntoRequest(baseSearchRequest, tableFilters, 'invoice');
    }
    return baseSearchRequest;
  }, [activeTab, baseSearchRequest, tableFilters]);

  // Check if we need to fetch filtered data (Table view + active filters)
  const shouldFetchFiltered = useMemo(() => activeTab === 'table' && activeFilterCount > 0, [activeTab, activeFilterCount]);

  // ========================================
  // SEQUENTIAL API LOADING FLOW - BASE DATA (ORIGINAL)
  // ========================================
  // CRITICAL: Always uses baseSearchRequest to get the "Original" values

  // Step 1: Base Invoice Data
  const {
    data: baseInvoiceData,
    isLoading: isBaseInvoiceDataLoading,
    isError: isBaseInvoiceDataError,
    isSuccess: isBaseInvoiceDataSuccess,
  } = useKeyMetricsInvoiceData('REPORT', baseSearchRequest);

  // Step 2: Base Shipment Data
  const {
    data: baseShipmentData,
    isLoading: isBaseShipmentDataLoading,
    isError: isBaseShipmentDataError,
    isSuccess: isBaseShipmentDataSuccess,
  } = useKeyMetricsShipmentData('REPORT', baseSearchRequest, {
    enabled: isBaseInvoiceDataSuccess,
  });

  // Step 3: Chart Data (uses chartSearchRequest)
  // Dependencies: Base Shipment Success
  const {
    data: chartData,
    isLoading: isChartLoading,
    isError: isChartError,
    isSuccess: isChartSuccess,
  } = useChart(chartSearchRequest, {
    enabled: isBaseShipmentDataSuccess,
  });

  // Step 4: Base Charge Data
  // Dependencies: Chart Success
  const {
    data: baseChargeData,
    isLoading: isBaseChargeDataLoading,
    isError: isBaseChargeDataError,
  } = useKeyMetricsChargeData('REPORT', baseSearchRequest, {
    enabled: isChartSuccess,
  });

  // Step 5: Filter Metadata (Allowed currencies/years)
  // Dependencies: Chart Success (Delayed to unblock page load)
  // We only need currencyReference and dateReference for this call
  const filterRequest = useMemo(
    () => ({
      currencyReference: baseSearchRequest.currencyReference,
      dateReference: baseSearchRequest.dateReference,
    }),
    [baseSearchRequest.currencyReference, baseSearchRequest.dateReference]
  );

  const { data: filterMetadata, isLoading: isLoadingFilters } = useFilters('REPORT', filterRequest, {
    enabled: isChartSuccess,
  });

  // ========================================
  // SEQUENTIAL API LOADING FLOW - FILTERED DATA
  // ========================================
  // CRITICAL: Only runs when shouldFetchFiltered is true
  // Follows similar sequential pattern but doesn't need to wait for Chart

  // Step 1: Filtered Invoice Data
  const {
    data: filteredInvoiceData,
    // isLoading: isFilteredInvoiceDataLoading, // Not tracking loading for filtered data to avoid blocking UI
    isSuccess: isFilteredInvoiceSuccess,
  } = useKeyMetricsInvoiceData('REPORT', finalSearchRequest, {
    enabled: shouldFetchFiltered,
  });

  // Step 2: Filtered Shipment Data
  const { data: filteredShipmentData, isSuccess: isFilteredShipmentSuccess } = useKeyMetricsShipmentData('REPORT', finalSearchRequest, {
    enabled: shouldFetchFiltered && isFilteredInvoiceSuccess,
  });

  // Step 3: Filtered Charge Data
  // Note: Skipping Chart step for filtered flow as chart data doesn't support table filters
  const { data: filteredChargeData } = useKeyMetricsChargeData('REPORT', finalSearchRequest, {
    enabled: shouldFetchFiltered && isFilteredShipmentSuccess,
  });

  // ========================================
  // DATA PROCESSING
  // ========================================

  // Show data progressively - don't wait for all APIs to complete
  const isKeyMetricsLoading = isBaseInvoiceDataLoading && isBaseShipmentDataLoading && isBaseChargeDataLoading;
  const hasAnyBaseData = !!(baseInvoiceData || baseShipmentData || baseChargeData);
  const hasAnyFilteredData = !!(filteredInvoiceData || filteredShipmentData || filteredChargeData);

  // Create individual loading state map for each card type
  const cardLoadingStates = useMemo(
    () => ({
      // Invoice-related cards
      [CardId.NO_INVOICES]: isBaseInvoiceDataLoading,
      [CardId.TOTAL_AMOUNT]: isBaseInvoiceDataLoading,

      // Shipment-related cards
      [CardId.NO_SHIPMENTS]: isBaseShipmentDataLoading,
      [CardId.VOLUME]: isBaseShipmentDataLoading,
      [CardId.GROSS_WEIGHT]: isBaseShipmentDataLoading,
      [CardId.CHARGABLE_WEIGHT]: isBaseShipmentDataLoading,
      [CardId.TOTAL_CO2_EMISSIONS]: isBaseShipmentDataLoading,

      // Charge-related cards
      [CardId.NO_CHARGES]: isBaseChargeDataLoading,
      [CardId.TOTAL_DEVIATION_AMOUNT]: isBaseChargeDataLoading,

      // File ingestion cards - not loaded by current APIs
      [CardId.NEW_ISSUES]: false,
      [CardId.ISSUES_UNDER_REVIEW]: false,
      [CardId.FILE_INGESTION_FAILURES]: false,
      [CardId.TOTAL_FILES_UPLOADED]: false,
      [CardId.EXTRACTED_PAGES]: false,
    }),
    [isBaseInvoiceDataLoading, isBaseShipmentDataLoading, isBaseChargeDataLoading]
  );

  // Create individual error state map for each card type
  const cardErrorStates = useMemo(
    () => ({
      // Invoice-related cards
      [CardId.NO_INVOICES]: isBaseInvoiceDataError,
      [CardId.TOTAL_AMOUNT]: isBaseInvoiceDataError,

      // Shipment-related cards
      [CardId.NO_SHIPMENTS]: isBaseShipmentDataError,
      [CardId.VOLUME]: isBaseShipmentDataError,
      [CardId.GROSS_WEIGHT]: isBaseShipmentDataError,
      [CardId.CHARGABLE_WEIGHT]: isBaseShipmentDataError,
      [CardId.TOTAL_CO2_EMISSIONS]: isBaseShipmentDataError,

      // Charge-related cards
      [CardId.NO_CHARGES]: isBaseChargeDataError,
      [CardId.TOTAL_DEVIATION_AMOUNT]: isBaseChargeDataError,

      // File ingestion cards - not loaded by current APIs
      [CardId.NEW_ISSUES]: false,
      [CardId.ISSUES_UNDER_REVIEW]: false,
      [CardId.FILE_INGESTION_FAILURES]: false,
      [CardId.TOTAL_FILES_UPLOADED]: false,
      [CardId.EXTRACTED_PAGES]: false,
    }),
    [isBaseInvoiceDataError, isBaseShipmentDataError, isBaseChargeDataError]
  );

  // Merge BASE key metrics data
  const keyMetricsResponse = useMemo<KeyMetricsResponse | undefined>(() => {
    if (!hasAnyBaseData) return undefined;

    return {
      keymetrics: {
        noInvoices: baseInvoiceData?.keymetrics?.noInvoices,
        totalAmount: baseInvoiceData?.keymetrics?.totalAmount,
        noShipments: baseShipmentData?.keymetrics?.noShipments,
        volume: baseShipmentData?.keymetrics?.volume,
        grossWeight: baseShipmentData?.keymetrics?.grossWeight,
        chargeableWeight: baseShipmentData?.keymetrics?.chargeableWeight,
        totalCo2Emissions: baseShipmentData?.keymetrics?.totalCo2Emissions,
        noCharges: baseChargeData?.keymetrics?.noCharges,
        totalDeviationAmount: baseChargeData?.keymetrics?.totalDeviationAmount,
      },
      meta: baseInvoiceData?.meta || baseShipmentData?.meta || baseChargeData?.meta,
    };
  }, [baseInvoiceData, baseShipmentData, baseChargeData, hasAnyBaseData]);

  // Merge FILTERED key metrics data
  const filteredKeyMetricsResponse = useMemo<KeyMetricsResponse | undefined>(() => {
    if (!shouldFetchFiltered || !hasAnyFilteredData) return undefined;

    return {
      keymetrics: {
        noInvoices: filteredInvoiceData?.keymetrics?.noInvoices,
        totalAmount: filteredInvoiceData?.keymetrics?.totalAmount,
        noShipments: filteredShipmentData?.keymetrics?.noShipments,
        volume: filteredShipmentData?.keymetrics?.volume,
        grossWeight: filteredShipmentData?.keymetrics?.grossWeight,
        chargeableWeight: filteredShipmentData?.keymetrics?.chargeableWeight,
        totalCo2Emissions: filteredShipmentData?.keymetrics?.totalCo2Emissions,
        noCharges: filteredChargeData?.keymetrics?.noCharges,
        totalDeviationAmount: filteredChargeData?.keymetrics?.totalDeviationAmount,
      },
      meta: filteredInvoiceData?.meta || filteredShipmentData?.meta || filteredChargeData?.meta,
    };
  }, [filteredInvoiceData, filteredShipmentData, filteredChargeData, shouldFetchFiltered, hasAnyFilteredData]);

  // Helper function to update totals from key metrics
  const updateTotalsFromMetrics = useCallback((metrics: KeyMetricsResponse | undefined) => {
    if (!metrics?.keymetrics) return;

    const { noInvoices, noShipments, noCharges } = metrics.keymetrics;

    if (noInvoices !== undefined && noInvoices !== null) {
      setTotals((prev) => ({ ...prev, invoices: noInvoices }));
    }
    if (noShipments !== undefined && noShipments !== null) {
      setTotals((prev) => ({ ...prev, shipments: noShipments }));
    }
    if (noCharges !== undefined && noCharges !== null) {
      setTotals((prev) => ({ ...prev, charges: noCharges }));
    }
  }, []);

  // Sync tab counters from key metrics data
  // Ensures invoice/charges/shipments tabs show correct counts from the current data source
  useEffect(() => {
    if (shouldFetchFiltered && filteredKeyMetricsResponse) {
      updateTotalsFromMetrics(filteredKeyMetricsResponse);
    } else if (!shouldFetchFiltered && keyMetricsResponse) {
      updateTotalsFromMetrics(keyMetricsResponse);
    }
  }, [shouldFetchFiltered, filteredKeyMetricsResponse, keyMetricsResponse, updateTotalsFromMetrics]);

  // Map to UI card items (pass both base and filtered responses)
  const keyMetrics = useMemo(() => keyMetricsMapper(keyMetricsResponse, false, filteredKeyMetricsResponse), [keyMetricsResponse, filteredKeyMetricsResponse]);

  // ========================================
  // CONTEXT VALUE
  // ========================================

  const contextValue = useMemo<AnalyticsDataContextValue>(
    () => ({
      keyMetricsResponse,
      filteredKeyMetricsResponse,
      // Individual API Responses
      baseInvoiceData,
      baseShipmentData,
      baseChargeData,
      filteredInvoiceData,
      filteredShipmentData,
      filteredChargeData,
      keyMetrics,
      chartData,
      filterMetadata,
      isLoadingFilters,
      activeTab,
      setActiveTab,
      isKeyMetricsLoading,
      isChartLoading,
      isChartError,
      cardLoadingStates,
      cardErrorStates,
      baseSearchRequest,
      chartSearchRequest,
      // Table filter state (merged from AnalyticsTableContext)
      filters: tableFilters,
      drawerFilters,
      activeFilterCount,
      updateFilter: handleColumnFilter,
      clearAllFilters,
      // Table totals
      totals,
      updateTotal,
      freshTabs,
    }),
    [
      keyMetricsResponse,
      filteredKeyMetricsResponse,
      baseInvoiceData,
      baseShipmentData,
      baseChargeData,
      filteredInvoiceData,
      filteredShipmentData,
      filteredChargeData,
      keyMetrics,
      chartData,
      filterMetadata,
      isLoadingFilters,
      activeTab,
      setActiveTab,
      isKeyMetricsLoading,
      isChartLoading,
      isChartError,
      cardLoadingStates,
      cardErrorStates,
      baseSearchRequest,
      chartSearchRequest,
      drawerFilters,
      tableFilters,
      activeFilterCount,
      handleColumnFilter,
      clearAllFilters,
      totals,
      updateTotal,
      freshTabs,
    ]
  );

  return <AnalyticsDataContext.Provider value={contextValue}>{children}</AnalyticsDataContext.Provider>;
};

/**
 * Hook to access analytics data context
 * Must be used within AnalyticsDataProvider
 */
export const useAnalyticsData = () => {
  const context = useContext(AnalyticsDataContext);
  if (!context) {
    throw new Error('useAnalyticsData must be used within AnalyticsDataProvider');
  }
  return context;
};
